/**
 * ExportCustomerManualPricesDialog — Aug 2026
 * ------------------------------------------------------------------
 * Super-admin only dialog for managing per-customer manual product
 * prices. When a manual price is set, the New Export Order modal
 * shows and uses it instead of the product's default.
 *
 * UX
 *   • Header shows the customer number + business name.
 *   • Add-a-row: SearchableSelect on products, price + currency inputs,
 *     "Save row" button.
 *   • Existing rows table (Item Code | Item Name | Manual Price |
 *     Currency | Actions) with a search box and inline edit.
 *   • CSV paste tab: textarea (`item_code,price,currency` per line),
 *     preview → apply. Backend returns per-row results so partial
 *     failures don't reject the whole batch.
 *
 * Backend endpoints
 *   • GET  /api/admin/export-customers/:id/manual-prices
 *   • PUT  /api/admin/export-customers/:id/manual-prices/:pid
 *   • DELETE .../manual-prices/:pid
 *   • POST .../manual-prices/bulk
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import {
  X, Search, Plus, Save, Trash2, Pencil, Loader2, DollarSign,
  Upload, FileText, CheckCircle2, AlertCircle,
} from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import CustomerNameLabel from './CustomerNameLabel';
import { useToast } from '../hooks/use-toast';

const CURRENCIES = ['OMR', 'USD', 'EUR', 'GBP', 'AED', 'SAR', 'INR'];

const parseCsvRows = (text) => {
  const lines = String(text || '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  const rows = [];
  const errors = [];
  const headerLike = lines[0]?.toLowerCase().replace(/\s/g, '');
  const skipHeader = headerLike?.startsWith('item_code') || headerLike?.startsWith('itemcode');
  const src = skipHeader ? lines.slice(1) : lines;
  src.forEach((line, i) => {
    const idx = skipHeader ? i + 1 : i;
    // Support tab, comma, or semicolon.
    const parts = line.split(/[,;\t]/).map((s) => s.trim());
    if (parts.length < 2) {
      errors.push({ line: idx + 1, error: 'Need at least item_code and price.' });
      return;
    }
    const [item_code, priceStr, currency] = parts;
    const price = Number(priceStr);
    if (!item_code) {
      errors.push({ line: idx + 1, error: 'Missing item_code.' });
      return;
    }
    if (!Number.isFinite(price) || price < 0) {
      errors.push({ line: idx + 1, error: `Invalid price '${priceStr}'.` });
      return;
    }
    rows.push({
      item_code,
      price,
      currency: (currency || 'OMR').toUpperCase(),
    });
  });
  return { rows, errors };
};

const ExportCustomerManualPricesDialog = ({
  API,
  authHeaders,
  customer,      // { id, customer_number, business_name, name }
  products,      // full product list (from parent)
  onClose,
  isSuperAdmin,
}) => {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyPid, setBusyPid] = useState('');
  const [tableSearch, setTableSearch] = useState('');
  const [editing, setEditing] = useState({}); // { [product_id]: {price, currency} }

  const [pickerQuery, setPickerQuery] = useState('');
  const [pickerProduct, setPickerProduct] = useState(null);
  const [newPrice, setNewPrice] = useState('');
  const [newCurrency, setNewCurrency] = useState('OMR');
  const [adding, setAdding] = useState(false);

  const [tab, setTab] = useState('single'); // 'single' | 'bulk'
  const [bulkText, setBulkText] = useState('');
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    if (!customer?.id) return;
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/export-customers/${customer.id}/manual-prices`,
        { headers: authHeaders },
      );
      setRows(data?.prices || []);
    } catch (err) {
      toast({
        title: 'Load failed',
        description: err?.response?.data?.detail || err?.message || 'Unknown error',
      });
    } finally {
      setLoading(false);
    }
  }, [API, authHeaders, customer?.id, toast]);

  useEffect(() => { load(); }, [load]);

  const usedProductIds = useMemo(
    () => new Set(rows.map((r) => r.product_id)),
    [rows],
  );

  const filteredPickerProducts = useMemo(() => {
    const q = pickerQuery.trim().toLowerCase();
    const base = (products || []).filter((p) => !usedProductIds.has(p.id));
    if (!q) return base.slice(0, 20);
    return base.filter((p) => (
      (p.name || '').toLowerCase().includes(q)
      || (p.item_code || '').toLowerCase().includes(q)
      || (p.sku || '').toLowerCase().includes(q)
    )).slice(0, 20);
  }, [products, pickerQuery, usedProductIds]);

  const filteredRows = useMemo(() => {
    const q = tableSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => (
      (r.item_code || '').toLowerCase().includes(q)
      || (r.product_name || '').toLowerCase().includes(q)
    ));
  }, [rows, tableSearch]);

  const addRow = async () => {
    if (!pickerProduct) { toast({ title: 'Pick a product first' }); return; }
    const price = Number(newPrice);
    if (!Number.isFinite(price) || price < 0) {
      toast({ title: 'Enter a valid price' });
      return;
    }
    setAdding(true);
    try {
      await axios.put(
        `${API}/admin/export-customers/${customer.id}/manual-prices/${pickerProduct.id}`,
        { price, currency: newCurrency },
        { headers: authHeaders },
      );
      toast({ title: 'Manual price saved', description: `${pickerProduct.name} · ${newCurrency} ${price.toFixed(3)}` });
      setPickerProduct(null);
      setPickerQuery('');
      setNewPrice('');
      setNewCurrency('OMR');
      load();
    } catch (err) {
      toast({
        title: 'Save failed',
        description: err?.response?.data?.detail || err?.message,
      });
    } finally { setAdding(false); }
  };

  const startEdit = (row) => {
    setEditing((prev) => ({
      ...prev,
      [row.product_id]: { price: String(row.price), currency: row.currency || 'OMR' },
    }));
  };

  const cancelEdit = (pid) => {
    setEditing((prev) => {
      const next = { ...prev };
      delete next[pid];
      return next;
    });
  };

  const saveEdit = async (row) => {
    const draft = editing[row.product_id];
    if (!draft) return;
    const price = Number(draft.price);
    if (!Number.isFinite(price) || price < 0) {
      toast({ title: 'Enter a valid price' });
      return;
    }
    setBusyPid(row.product_id);
    try {
      await axios.put(
        `${API}/admin/export-customers/${customer.id}/manual-prices/${row.product_id}`,
        { price, currency: draft.currency },
        { headers: authHeaders },
      );
      toast({ title: 'Updated' });
      cancelEdit(row.product_id);
      load();
    } catch (err) {
      toast({
        title: 'Update failed',
        description: err?.response?.data?.detail || err?.message,
      });
    } finally { setBusyPid(''); }
  };

  const removeRow = async (row) => {
    if (!window.confirm(`Remove manual price for ${row.product_name}?`)) return;
    setBusyPid(row.product_id);
    try {
      await axios.delete(
        `${API}/admin/export-customers/${customer.id}/manual-prices/${row.product_id}`,
        { headers: authHeaders },
      );
      toast({ title: 'Removed' });
      load();
    } catch (err) {
      toast({
        title: 'Remove failed',
        description: err?.response?.data?.detail || err?.message,
      });
    } finally { setBusyPid(''); }
  };

  const runBulkPreview = () => {
    const parsed = parseCsvRows(bulkText);
    setBulkPreview(parsed);
    setBulkResult(null);
  };

  const applyBulk = async () => {
    if (!bulkPreview?.rows?.length) {
      toast({ title: 'Nothing to apply', description: 'Paste rows and click Preview first.' });
      return;
    }
    setBulkBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/export-customers/${customer.id}/manual-prices/bulk`,
        { rows: bulkPreview.rows },
        { headers: authHeaders },
      );
      setBulkResult(data);
      toast({
        title: 'Bulk upsert done',
        description: `${data.inserted + data.updated}/${data.total} applied · ${data.failed} failed`,
      });
      load();
    } catch (err) {
      toast({
        title: 'Bulk failed',
        description: err?.response?.data?.detail || err?.message,
      });
    } finally { setBulkBusy(false); }
  };

  if (!customer) return null;

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4"
      data-testid="export-customer-manual-prices-dialog"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between bg-gradient-to-r from-[#0078D4]/5 to-transparent">
          <div>
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-[#0078D4]" />
              <h2 className="text-lg font-bold text-[#0F172A]">Manual Prices</h2>
            </div>
            <div className="text-xs text-neutral-500 mt-1">
              <span className="font-mono font-bold text-[#0078D4]">{customer.customer_number}</span>
              {' · '}
              <CustomerNameLabel customer={customer} inline />
              {' · Overrides the default product price on New Export Order for this customer.'}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-700 p-1"
            aria-label="Close"
            data-testid="manual-prices-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {!isSuperAdmin && (
          <div className="mx-6 mt-4 bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-lg p-3">
            <b>Read-only.</b> Only super admins can add, edit, or remove manual prices.
          </div>
        )}

        {/* Tabs */}
        {isSuperAdmin && (
          <div className="px-6 pt-4">
            <div className="inline-flex rounded-full bg-neutral-100 p-1 text-xs font-bold">
              <button
                type="button"
                onClick={() => setTab('single')}
                className={`px-4 py-1.5 rounded-full transition ${tab === 'single' ? 'bg-white text-[#0078D4] shadow-sm' : 'text-neutral-500 hover:text-neutral-700'}`}
                data-testid="manual-prices-tab-single"
              >
                Single row
              </button>
              <button
                type="button"
                onClick={() => setTab('bulk')}
                className={`px-4 py-1.5 rounded-full transition ${tab === 'bulk' ? 'bg-white text-[#0078D4] shadow-sm' : 'text-neutral-500 hover:text-neutral-700'}`}
                data-testid="manual-prices-tab-bulk"
              >
                CSV / paste
              </button>
            </div>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {isSuperAdmin && tab === 'single' && (
            <div className="bg-white rounded-2xl border border-[#0078D4]/30 p-4" data-testid="manual-prices-add-form">
              <div className="text-xs font-semibold text-neutral-700 uppercase tracking-wide mb-2 flex items-center gap-1">
                <Plus className="w-3.5 h-3.5" /> Add a manual price
              </div>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-start">
                <div className="md:col-span-6 relative">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <Input
                      value={pickerProduct ? `${pickerProduct.name}${pickerProduct.item_code || pickerProduct.sku ? ` (${pickerProduct.item_code || pickerProduct.sku})` : ''}` : pickerQuery}
                      onChange={(e) => { setPickerQuery(e.target.value); setPickerProduct(null); }}
                      placeholder="Search product by name, item code, or SKU…"
                      className="pl-9"
                      data-testid="manual-prices-picker-input"
                    />
                  </div>
                  {!pickerProduct && pickerQuery && (
                    <div className="absolute left-0 right-0 top-full mt-1 z-20 bg-white rounded-lg border border-neutral-200 shadow-lg max-h-56 overflow-y-auto divide-y divide-neutral-100">
                      {filteredPickerProducts.length === 0 ? (
                        <div className="p-3 text-xs text-neutral-400">No matches (or already in the list below).</div>
                      ) : filteredPickerProducts.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => { setPickerProduct(p); setPickerQuery(''); }}
                          className="w-full text-left px-3 py-2 hover:bg-neutral-50 text-sm"
                          data-testid={`manual-prices-picker-opt-${p.id}`}
                        >
                          <div className="font-semibold text-[#0F172A]">{p.name}</div>
                          <div className="text-[11px] font-mono text-neutral-500">
                            {p.item_code || p.sku || p.id} · default OMR {Number(p.price || 0).toFixed(3)}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="md:col-span-3">
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={newPrice}
                    onChange={(e) => setNewPrice(e.target.value.replace(/[^\d.]/g, ''))}
                    placeholder="Price (e.g. 1.250)"
                    className="tabular-nums"
                    data-testid="manual-prices-add-price"
                  />
                </div>
                <div className="md:col-span-2">
                  <select
                    value={newCurrency}
                    onChange={(e) => setNewCurrency(e.target.value)}
                    className="w-full h-10 rounded-md border border-neutral-200 px-3 text-sm bg-white focus:outline-none focus:border-[#0078D4]"
                    data-testid="manual-prices-add-currency"
                  >
                    {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="md:col-span-1 flex items-center justify-end">
                  <Button
                    onClick={addRow}
                    disabled={adding || !pickerProduct}
                    className="bg-[#0078D4] hover:bg-[#005A9E] text-white w-full md:w-auto h-10"
                    data-testid="manual-prices-add-save"
                    title="Save row"
                  >
                    {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {isSuperAdmin && tab === 'bulk' && (
            <div className="bg-white rounded-2xl border border-[#0078D4]/30 p-4" data-testid="manual-prices-bulk">
              <div className="text-xs font-semibold text-neutral-700 uppercase tracking-wide mb-2 flex items-center gap-1">
                <Upload className="w-3.5 h-3.5" /> CSV paste
              </div>
              <p className="text-[11px] text-neutral-500 mb-2">
                One row per line: <code className="bg-neutral-100 px-1 rounded">item_code, price, currency</code>. The header row is optional. Comma, semicolon, or tab work as separators.
              </p>
              <textarea
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                placeholder={"item_code,price,currency\nSKU-0001,1.250,OMR\nSKU-0002,0.800,OMR"}
                className="w-full h-32 rounded-lg border border-neutral-200 p-3 font-mono text-xs focus:outline-none focus:border-[#0078D4]"
                data-testid="manual-prices-bulk-textarea"
              />
              <div className="flex gap-2 mt-2">
                <Button
                  variant="outline"
                  onClick={runBulkPreview}
                  disabled={!bulkText.trim()}
                  data-testid="manual-prices-bulk-preview"
                >
                  <FileText className="w-4 h-4 mr-1" /> Preview
                </Button>
                <Button
                  onClick={applyBulk}
                  disabled={bulkBusy || !bulkPreview?.rows?.length}
                  className="bg-[#0078D4] hover:bg-[#005A9E] text-white"
                  data-testid="manual-prices-bulk-apply"
                >
                  {bulkBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                  Apply {bulkPreview?.rows?.length || 0} row{bulkPreview?.rows?.length === 1 ? '' : 's'}
                </Button>
              </div>
              {bulkPreview && (
                <div className="mt-3 text-xs">
                  {bulkPreview.errors.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-2 mb-2">
                      <div className="font-bold text-red-700 mb-1">Skipped {bulkPreview.errors.length} line{bulkPreview.errors.length === 1 ? '' : 's'}:</div>
                      <ul className="list-disc pl-4 text-red-700">
                        {bulkPreview.errors.slice(0, 6).map((er, i) => (
                          <li key={i}>Line {er.line}: {er.error}</li>
                        ))}
                        {bulkPreview.errors.length > 6 && <li>… and {bulkPreview.errors.length - 6} more</li>}
                      </ul>
                    </div>
                  )}
                  <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-2">
                    <div className="font-bold text-neutral-700 mb-1">Ready to apply: {bulkPreview.rows.length}</div>
                    {bulkPreview.rows.slice(0, 6).map((r, i) => (
                      <div key={i} className="font-mono">{r.item_code} → {r.currency} {r.price.toFixed(3)}</div>
                    ))}
                    {bulkPreview.rows.length > 6 && <div>… and {bulkPreview.rows.length - 6} more</div>}
                  </div>
                </div>
              )}
              {bulkResult && (
                <div className="mt-3 text-xs bg-neutral-50 border border-neutral-200 rounded-lg p-2 max-h-60 overflow-y-auto" data-testid="manual-prices-bulk-result">
                  <div className="font-bold text-neutral-700 mb-2">
                    Applied {bulkResult.inserted + bulkResult.updated}/{bulkResult.total} · {bulkResult.failed} failed
                  </div>
                  {(bulkResult.results || []).map((r, i) => (
                    <div key={i} className={`flex items-center gap-1 py-0.5 ${r.ok ? 'text-emerald-700' : 'text-red-700'}`}>
                      {r.ok ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                      <span className="font-mono">{r.item_code}</span>
                      {r.ok ? <span>· {r.currency} {Number(r.price).toFixed(3)} · {r.product_name}</span>
                            : <span>· {r.error}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Table */}
          <div>
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="relative flex-1 max-w-md">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <Input
                  value={tableSearch}
                  onChange={(e) => setTableSearch(e.target.value)}
                  placeholder="Search by item code or name"
                  className="pl-9"
                  data-testid="manual-prices-search"
                />
              </div>
              <span className="text-xs text-neutral-500" data-testid="manual-prices-count">
                {loading ? '…' : `${filteredRows.length} of ${rows.length}`}
              </span>
            </div>

            {loading ? (
              <div className="p-8 text-center text-neutral-500 text-sm">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="bg-white rounded-xl border border-dashed border-neutral-300 p-8 text-center" data-testid="manual-prices-empty">
                <div className="text-sm font-semibold text-neutral-700">No manual prices yet</div>
                <div className="text-xs text-neutral-500 mt-1">
                  {isSuperAdmin
                    ? 'Add a row above (or paste CSV) — the New Export Order modal will use it for this customer.'
                    : 'Ask a super admin to set custom prices for this customer.'}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-neutral-200 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-neutral-50 border-b border-neutral-200">
                    <tr className="text-left text-[10px] uppercase tracking-wider text-neutral-500 font-bold">
                      <th className="p-3">Item Code</th>
                      <th className="p-3">Item Name</th>
                      <th className="p-3 text-right">Default</th>
                      <th className="p-3">Manual Price</th>
                      <th className="p-3">Currency</th>
                      <th className="p-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100">
                    {filteredRows.map((r) => {
                      const isEditing = !!editing[r.product_id];
                      const draft = editing[r.product_id] || {};
                      return (
                        <tr key={r.product_id} data-testid={`manual-price-row-${r.product_id}`}>
                          <td className="p-3 font-mono text-xs">{r.item_code || '—'}</td>
                          <td className="p-3 font-semibold text-[#0F172A]">{r.product_name}</td>
                          <td className="p-3 text-right text-xs text-neutral-500 tabular-nums">
                            OMR {r.default_price.toFixed(3)}
                          </td>
                          <td className="p-3">
                            {isEditing ? (
                              <Input
                                type="text"
                                inputMode="decimal"
                                value={draft.price}
                                onChange={(e) => setEditing((prev) => ({
                                  ...prev,
                                  [r.product_id]: { ...prev[r.product_id], price: e.target.value.replace(/[^\d.]/g, '') },
                                }))}
                                className="h-8 w-28 tabular-nums"
                                data-testid={`manual-price-edit-price-${r.product_id}`}
                              />
                            ) : (
                              <span className="font-bold text-[#0078D4] tabular-nums" data-testid={`manual-price-value-${r.product_id}`}>
                                {r.price.toFixed(3)}
                              </span>
                            )}
                          </td>
                          <td className="p-3">
                            {isEditing ? (
                              <select
                                value={draft.currency}
                                onChange={(e) => setEditing((prev) => ({
                                  ...prev,
                                  [r.product_id]: { ...prev[r.product_id], currency: e.target.value },
                                }))}
                                className="h-8 rounded-md border border-neutral-200 px-2 text-xs bg-white"
                                data-testid={`manual-price-edit-currency-${r.product_id}`}
                              >
                                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                              </select>
                            ) : (
                              <span className="font-mono text-xs">{r.currency}</span>
                            )}
                          </td>
                          <td className="p-3">
                            <div className="flex justify-end gap-1">
                              {isSuperAdmin && !isEditing && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => startEdit(r)}
                                    className="text-xs px-2 py-1 rounded-full bg-white border border-neutral-200 hover:bg-neutral-50 text-neutral-700 inline-flex items-center gap-1"
                                    data-testid={`manual-price-edit-btn-${r.product_id}`}
                                  >
                                    <Pencil className="w-3 h-3" /> Edit
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => removeRow(r)}
                                    disabled={busyPid === r.product_id}
                                    className="text-xs px-2 py-1 rounded-full bg-white border border-red-200 hover:bg-red-50 text-red-700 inline-flex items-center gap-1 disabled:opacity-40"
                                    data-testid={`manual-price-delete-btn-${r.product_id}`}
                                  >
                                    {busyPid === r.product_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                                    Remove
                                  </button>
                                </>
                              )}
                              {isSuperAdmin && isEditing && (
                                <>
                                  <Button
                                    onClick={() => saveEdit(r)}
                                    disabled={busyPid === r.product_id}
                                    className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 rounded-full px-3 text-xs"
                                    data-testid={`manual-price-save-${r.product_id}`}
                                  >
                                    {busyPid === r.product_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                                    Save
                                  </Button>
                                  <Button
                                    variant="outline"
                                    onClick={() => cancelEdit(r.product_id)}
                                    className="h-8 rounded-full px-3 text-xs"
                                    data-testid={`manual-price-cancel-${r.product_id}`}
                                  >
                                    Cancel
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-neutral-200 bg-neutral-50 flex justify-end">
          <Button variant="outline" onClick={onClose} data-testid="manual-prices-done">
            Done
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExportCustomerManualPricesDialog;
